import { cn } from '@/utils/cn';
import { ErrorMessage } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select, { MultiValue, SingleValue, StylesConfig } from 'react-select';

export type OptionType = {
  label: string;
  value: string | number | boolean;
  visible?: boolean;
};

interface SelectInputProps {
  name: string;
  defaultValue:
    | string
    | number
    | boolean
    | (string | number | boolean)[]
    | null;
  isDisabled?: boolean;
  isLoading?: boolean;
  isClearable?: boolean;
  isRtl?: boolean;
  isSearchable?: boolean;
  options: OptionType[];
  error?: string;
  onChange?: (event: {
    target: {
      name: string;
      value: string | number | boolean | (string | number | boolean)[];
    };
  }) => void;
  isMulti?: boolean;
  placeholder?: string;
  isRequired?: boolean;
  label?: string;
  containerStyle?: string;
  labelStyle?: string;
  useFormik?: boolean;
  menuPortalTarget?: HTMLElement;
}

export const SelectInput = ({
  name,
  defaultValue,
  isDisabled,
  isLoading,
  isClearable,
  isRtl,
  isSearchable,
  options = [],
  onChange,
  isMulti = false,
  placeholder,
  isRequired,
  label,
  containerStyle = '',
  labelStyle = '',
  error,
  useFormik = true,
  menuPortalTarget,
}: SelectInputProps) => {
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState<
    SingleValue<OptionType> | MultiValue<OptionType>
  >(null);

  // Préchargement de la valeur par défaut
  useEffect(() => {
    if (options.length) {
      const value = isMulti
        ? ((defaultValue as (string | number | boolean)[])
            ?.map(value => options.find(option => option.value === value))
            .filter(Boolean) as OptionType[])
        : options.find(option => option.value === defaultValue) || null;

      setSelectedValue(value);
    }
  }, [options, defaultValue, isMulti]);

  // Gestion du changement de sélection
  const handleChange = (
    newValue: SingleValue<OptionType> | MultiValue<OptionType>,
  ) => {
    setSelectedValue(newValue);

    if (onChange) {
      const value = isMulti
        ? (newValue as OptionType[]).map(option => option.value)
        : ((newValue as OptionType)?.value ?? null);
      onChange({ target: { name, value } });
    }
  };

  // Styles personnalisés
  const selectStyles: StylesConfig<OptionType, boolean> = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '40px',
      height: '40px',
      border: error ? '1px solid #ef4444' : '1px solid #d1d5db',
      borderRadius: '0.5rem',
      boxShadow: error
        ? '0 0 0 1px #ef4444'
        : state.isFocused
          ? '0 0 0 2px rgba(14, 165, 233, 0.25)'
          : 'none',
      backgroundColor: isDisabled ? '#f3f4f6' : 'white',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      '&:hover': {
        borderColor: error
          ? '#ef4444'
          : state.isFocused
            ? '#0ea5e9'
            : '#9ca3af',
      },
      padding: '2px 8px',
      transition: 'all 0.2s ease',
    }),
    option: (provided, state) => ({
      ...provided,
      color: state.isSelected ? '#0ea5e9' : '#4b5563',
      backgroundColor: state.isSelected ? '#f0f9ff' : 'transparent',
      '&:hover': { backgroundColor: '#f0f9ff' },
      padding: '8px 12px',
    }),
    placeholder: provided => ({
      ...provided,
      color: '#9ca3af',
    }),
    singleValue: provided => ({
      ...provided,
      color: '#1f2937',
    }),
    multiValue: provided => ({
      ...provided,
      backgroundColor: '#f0f9ff',
      borderRadius: '0.25rem',
    }),
    multiValueLabel: provided => ({
      ...provided,
      color: '#0ea5e9',
      fontWeight: 500,
      padding: '2px 6px',
    }),
    multiValueRemove: provided => ({
      ...provided,
      color: '#0ea5e9',
      '&:hover': {
        backgroundColor: '#e0f2fe',
        color: '#0369a1',
      },
    }),
    menu: provided => ({
      ...provided,
      borderRadius: '0.5rem',
      boxShadow:
        '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      zIndex: 10,
    }),
    menuList: provided => ({
      ...provided,
      padding: '4px',
    }),
    indicatorSeparator: provided => ({
      ...provided,
      backgroundColor: '#d1d5db',
    }),
    dropdownIndicator: provided => ({
      ...provided,
      color: '#9ca3af',
      '&:hover': {
        color: '#6b7280',
      },
    }),
    clearIndicator: provided => ({
      ...provided,
      color: '#9ca3af',
      '&:hover': {
        color: '#6b7280',
      },
    }),
  };

  // Messages
  const noOptionsMessage = () => t('selectInput:no-options');
  const loadingMessage = () => t('selectInput:loading');
  const defaultPlaceholder = isMulti
    ? t('selectInput:placeholder.multi')
    : t('selectInput:placeholder.single');

  // Filtrer les options pour n'afficher que celles qui sont visibles
  const visibleOptions = options.filter(option => option.visible !== false);

  return (
    <div className={cn(`relative flex flex-col ${containerStyle}`)}>
      {label && (
        <label
          className={cn(`mb-2 text-xs font-medium text-gray-600 ${labelStyle}`)}
        >
          {label}
          {isRequired && <span className="ml-1 text-danger">*</span>}
        </label>
      )}
      <Select<OptionType>
        classNamePrefix="react-select"
        value={selectedValue}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isClearable={isClearable}
        isRtl={isRtl}
        isSearchable={isSearchable}
        name={name}
        options={visibleOptions}
        onChange={handleChange}
        isMulti={isMulti as unknown as undefined}
        placeholder={placeholder || defaultPlaceholder}
        noOptionsMessage={noOptionsMessage}
        loadingMessage={loadingMessage}
        styles={{
          ...selectStyles,
          menuPortal: base => ({
            ...base,
            zIndex: 99999,
          }),
        }}
        menuPortalTarget={menuPortalTarget}
        menuPosition="fixed"
        theme={theme => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary25: '#f0f9ff',
            primary: '#0ea5e9',
          },
        })}
      />
      {useFormik ? (
        <ErrorMessage
          name={name}
          component="span"
          className="mt-1 text-xs text-danger md:text-sm"
        />
      ) : (
        error && (
          <span className="mt-1 text-xs text-danger md:text-sm">{error}</span>
        )
      )}
    </div>
  );
};
